import { useCallback, useContext, useEffect, useMemo } from 'react';
import { gql } from '@apollo/client';
import { ClientErrorLogSeverity, useLogErrorMutation } from '../models/graphql';
import { AppContext } from '../providers/AppProvider';
import { useAuth } from './useAuth';

gql`
  mutation LogError(
    $workspaceId: ID!
    $message: String!
    $url: String!
    $stackTrace: String!
    $severity: ClientErrorLogSeverity!
  ) {
    logError(workspaceId: $workspaceId, message: $message, url: $url, stackTrace: $stackTrace, severity: $severity)
  }
`;

export const useLogErrorToServer = () => {
  const { isLoggedIn } = useAuth();
  const { selectedWorkspaceId } = useContext(AppContext);
  const [logErrorMutation] = useLogErrorMutation();
  // useCallback to satisfy exhaustive-deps rule
  const logErrorMessage = useCallback(
    (message: string, stackTrace?: string, severity: ClientErrorLogSeverity = ClientErrorLogSeverity.ERROR) => {
      if (isLoggedIn && !!message) {
        logErrorMutation({
          variables: {
            workspaceId: selectedWorkspaceId,
            message,
            url: window.location.href,
            stackTrace: stackTrace || new Error().stack || '',
            severity
          }
        }).catch(() => {
          // Catch errors so we don't end up in an infinite loop of trying to log an error -> server error -> global
          // error logic catches it -> logs it -> server error -> global error logging catches it -> etc.
        });
      }
    },
    [isLoggedIn, logErrorMutation, selectedWorkspaceId]
  );

  const logErrorObject = useCallback(
    (error: Error, severity: ClientErrorLogSeverity = ClientErrorLogSeverity.ERROR) => {
      logErrorMessage(error.message, error.stack, severity);
    },
    [logErrorMessage]
  );

  // memo-ise to make stable
  return useMemo(
    () => ({
      logErrorMessage,
      logErrorObject
    }),
    [logErrorMessage, logErrorObject]
  );
};

export const useGlobalErrorLogging = () => {
  const { logErrorMessage, logErrorObject } = useLogErrorToServer();

  const handleError = useCallback(
    (event: ErrorEvent) => {
      const { error } = event;

      // Log the error details to server
      if (error) {
        logErrorObject(error, ClientErrorLogSeverity.CRITICAL);
      }
    },
    [logErrorObject]
  );

  const handleUnhandledRejection = useCallback(
    (event: PromiseRejectionEvent) => {
      const reason = event.reason;

      if (reason instanceof Error) {
        logErrorObject(reason);
      } else {
        logErrorMessage('Promise Rejection');
      }
    },
    [logErrorMessage, logErrorObject]
  );

  useEffect(() => {
    // Set up the global error handlers
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Clean up the listeners when the component using the hook is unmounted
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [handleError, handleUnhandledRejection]);
};
